import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import {
  hasAuthorityCopilotPlanAccess,
  readUserFromStorage,
  getSubscriptionTier,
} from '../../utils/subscription';

const UNLOCKED_PREVIEW_ITEMS = [
  'Review insurance and cash buffer requirements',
  'Get your personalized filings checklist',
  'See a timeline and cost estimate',
  'Get recommended next actions inside Sovrynn',
];

const LOCKED_FEATURE_ITEMS = [
  'Personalized filings checklist',
  'Timeline and cost estimator',
  'Insurance and cash buffer guidance',
  'Recommended next actions inside Sovrynn',
];

const DISCLAIMER =
  'For educational and planning purposes only. Does not constitute legal, financial, or regulatory advice. Always verify with FMCSA and your state agencies.';

export default function AuthorityCopilotPromoSection({ user }) {
  const navigate = useNavigate();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [liveUser, setLiveUser] = useState(() => readUserFromStorage() ?? user);

  const refreshSubscriptionFromStorage = useCallback(() => {
    const next = readUserFromStorage();
    getSubscriptionTier(next);
    setLiveUser(next);
  }, []);

  useEffect(() => {
    window.addEventListener('focus', refreshSubscriptionFromStorage);
    window.addEventListener('storage', refreshSubscriptionFromStorage);
    return () => {
      window.removeEventListener('focus', refreshSubscriptionFromStorage);
      window.removeEventListener('storage', refreshSubscriptionFromStorage);
    };
  }, [refreshSubscriptionFromStorage]);

  const paid = hasAuthorityCopilotPlanAccess(liveUser);

  if (paid) {
    return (
      <section
        className="dashboard-pillar-card card authority-copilot-promo-card"
        aria-labelledby="authority-copilot-promo-title"
      >
        <h2 id="authority-copilot-promo-title" className="authority-copilot-promo-card__title">
          Ready to get your authority?
        </h2>
        <p className="authority-copilot-promo-card__body">
          The Authority Setup Copilot walks you through insurance requirements, cash reserves, onboarding expectations,
          FMCSA filings, and compliance basics — so you show up prepared.
        </p>
        <ul className="authority-copilot-promo-card__checklist authority-copilot-promo-card__checklist--preview">
          {UNLOCKED_PREVIEW_ITEMS.map((text) => (
            <li key={text}>
              <Check className="authority-copilot-promo-card__check-icon" size={18} strokeWidth={2.5} aria-hidden />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="authority-copilot-promo-card__disclaimer">{DISCLAIMER}</p>
        <div className="authority-copilot-promo-card__actions">
          <button
            type="button"
            className="btn btn-primary authority-copilot-promo-card__btn-primary"
            onClick={() => navigate('/authority-copilot')}
          >
            Launch Authority Setup Copilot
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="dashboard-pillar-card card authority-copilot-promo-card"
      aria-labelledby="authority-copilot-promo-title-locked"
    >
      <h2 id="authority-copilot-promo-title-locked" className="authority-copilot-promo-card__title">
        Ready to get your authority?
      </h2>
      <p className="authority-copilot-promo-card__body">
        The Authority Setup Copilot walks you through everything you need to prepare your FMCSA application —
        available on Owner-Operator and Fleet/Pro plans.
      </p>
      <div className="authority-copilot-promo-card__locked-wrap">
        <ul className="authority-copilot-promo-card__checklist authority-copilot-promo-card__checklist--locked">
          {LOCKED_FEATURE_ITEMS.map((text) => (
            <li key={text}>
              <Lock className="authority-copilot-promo-card__lock-icon" size={18} strokeWidth={2.25} aria-hidden />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="authority-copilot-promo-card__disclaimer">{DISCLAIMER}</p>
      <div className="authority-copilot-promo-card__actions authority-copilot-promo-card__actions--split">
        <button
          type="button"
          className="btn btn-primary authority-copilot-promo-card__btn-primary"
          onClick={() => navigate('/pricing')}
        >
          Upgrade to Unlock
        </button>
        <button
          type="button"
          className="btn btn-secondary authority-copilot-promo-card__btn-secondary"
          onClick={() => setLearnMoreOpen((o) => !o)}
          aria-expanded={learnMoreOpen}
        >
          Learn More
          {learnMoreOpen ? <ChevronUp size={18} aria-hidden /> : <ChevronDown size={18} aria-hidden />}
        </button>
      </div>
      {learnMoreOpen && (
        <div className="authority-copilot-promo-card__learn-more" role="region" aria-label="About Authority Setup Copilot">
          <p>
            The copilot is a guided, step-by-step review of insurance and cash buffer realities, onboarding expectations,
            FMCSA filing context, and basic compliance topics. When you upgrade, you can work through it in the app,
            then get a personalized filings checklist, timeline and cost estimate, and suggested next actions in
            Sovrynn — still for education and planning, not legal or filing advice.
          </p>
        </div>
      )}
    </section>
  );
}
